/**
 * ColorPickerGrid  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";

import { isFunction } from "@adapptio/lib-shared";

import { ColorPickerGridBase } from "./ColorPickerGridBase";
import { useColorPickerContext } from "./ColorPickerContext";
import { IColoColorPickerSpectrumBaseProps } from "./ColorPickerSpectrumBase";

export interface IColorPickerGridProps {
	value?: string;
	onChange?: (value: string) => void;
	className?: string;
}

export const ColorPickerGrid: React.FC<IColorPickerGridProps> = ({ className, value, onChange }) => {
	const { pickedColor, onChange: globalOnChange, onClose } = useColorPickerContext();
	const color = value || pickedColor;

	const handleChange = useCallback<IColoColorPickerSpectrumBaseProps["onChange"]>(
		(color) => {
			const val =
				color.rgb.a < 1
					? `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`
					: color.hex;

			if (isFunction(onChange)) {
				onChange(val);
			}

			if (isFunction(globalOnChange)) {
				globalOnChange(val);
			}
		},
		[ globalOnChange, onChange ]
	);

	return <ColorPickerGridBase className={className} color={color} onChange={handleChange} />;
};
